import { Fragment, useEffect, useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  ADMIN_CPM_CAMPAIGN_CHANNEL_REQUESTS,
  ADMIN_REVIEW_CPM_CAMPAIGN_CHANNEL_REQUEST,
  ADMIN_REVOKE_CPM_CAMPAIGN_CHANNEL_REQUEST,
  ADMIN_INVITE_CPM_CAMPAIGN_USER,
} from '@/queries/queries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { formatDateTime } from '@/lib/format';

type StatusFilter = 'pending' | 'approved' | 'rejected' | 'all';
const STATUS_FILTERS: readonly StatusFilter[] = ['pending', 'approved', 'rejected', 'all'] as const;
const COL_SPAN = 7;
const PAGE_SIZE = 25;

export default function AdminCampaignChannelRequestsTab({ campaignId }: { campaignId: string }) {
  const { t } = useTranslation();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(h);
  }, [search]);

  const queryVars = {
    campaignId,
    status: statusFilter,
    search: debouncedSearch || null,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };

  const { data, loading } = useQuery(ADMIN_CPM_CAMPAIGN_CHANNEL_REQUESTS, {
    variables: queryVars,
    fetchPolicy: 'cache-and-network',
  });

  const [reviewReason, setReviewReason] = useState<Record<string, string>>({});
  const [openInline, setOpenInline] = useState<Record<string, 'approve' | 'reject' | 'revoke' | null>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [inviteEmail, setInviteEmail] = useState('');
  const [invitePlatform, setInvitePlatform] = useState<'youtube' | 'instagram' | 'tiktok'>('youtube');
  const [inviteChannelUrl, setInviteChannelUrl] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);

  const [approve] = useMutation(ADMIN_REVIEW_CPM_CAMPAIGN_CHANNEL_REQUEST);
  const [revoke] = useMutation(ADMIN_REVOKE_CPM_CAMPAIGN_CHANNEL_REQUEST);
  const [invite, { loading: inviteLoading }] = useMutation(ADMIN_INVITE_CPM_CAMPAIGN_USER);

  const refetch = [{ query: ADMIN_CPM_CAMPAIGN_CHANNEL_REQUESTS, variables: queryVars }];

  const onApprove = async (reqId: string) => {
    const reason = reviewReason[reqId]?.trim() || null;
    try {
      await approve({
        variables: { id: reqId, status: 'approved', reason },
        refetchQueries: refetch,
      });
      setOpenInline((s) => ({ ...s, [reqId]: null }));
      setReviewReason((r) => ({ ...r, [reqId]: '' }));

    } catch (err: any) {
      setErrorMessage(err?.message || t('common.error', { defaultValue: 'Error' }));
    }
  };

  const onReject = async (reqId: string) => {
    const reason = reviewReason[reqId]?.trim();
    if (!reason) {
      setErrorMessage(t('admin.campaigns.channelRequests.reasonRequired'));
      return;
    }
    try {
      await approve({
        variables: { id: reqId, status: 'rejected', reason },
        refetchQueries: refetch,
      });
      setOpenInline((s) => ({ ...s, [reqId]: null }));
      setReviewReason((r) => ({ ...r, [reqId]: '' }));
     
    } catch (err: any) {
      setErrorMessage(err?.message || t('common.error', { defaultValue: 'Error' }));
    }
  };

  const onRevoke = async (reqId: string) => {
    const reason = reviewReason[reqId]?.trim();
    if (!reason) {
      setErrorMessage(t('admin.campaigns.channelRequests.reasonRequired'));
      return;
    }
    try {
      await revoke({
        variables: { id: reqId, reason },
        refetchQueries: refetch,
      });
      setOpenInline((s) => ({ ...s, [reqId]: null }));
      setReviewReason((r) => ({ ...r, [reqId]: '' }));
     
    } catch (err: any) {
      setErrorMessage(err?.message || t('common.error', { defaultValue: 'Error' }));
    }
  };

  const onInvite = async () => {
    const email = inviteEmail.trim();
    const channelUrl = inviteChannelUrl.trim();
    setInviteSuccess(null);
    if (!email || !channelUrl) {
      setErrorMessage(t('admin.campaigns.channelRequests.invite.missingFields', {
        defaultValue: 'Email and channel URL are required.',
      }));
      return;
    }
    try {
      await invite({
        variables: {
          campaignId, email, platform: invitePlatform, channelUrl,
        },
        refetchQueries: refetch,
      });
      setInviteSuccess(t('admin.campaigns.channelRequests.invite.success', {
        defaultValue: 'User added to the campaign.',
      }));
      setInviteEmail('');
      setInviteChannelUrl('');
    } catch (err: any) {
      setErrorMessage(err?.message || t('common.error', { defaultValue: 'Error' }));
    }
  };


  const rows: any[] = data?.adminCpmCampaignChannelRequests?.data || [];
  const total = data?.adminCpmCampaignChannelRequests?.total ?? 0;
  const totalPages = data?.adminCpmCampaignChannelRequests?.pageCount ?? 1;

  const handleStatusFilterChange = (nextStatus: StatusFilter) => {
    setStatusFilter(nextStatus);
    setPage(1);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border p-3 space-y-3">
        <div>
          <p className="text-sm font-semibold">
            {t('admin.campaigns.channelRequests.invite.title', { defaultValue: 'Add a user to this campaign' })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('admin.campaigns.channelRequests.invite.help', {
              defaultValue: "Creates an approved channel request so the user can participate — for invite-only campaigns. Enter the user's channel / profile URL (required for YouTube channel matching).",
            })}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <Input
            type="email"
            placeholder={t('admin.campaigns.channelRequests.invite.emailPlaceholder', { defaultValue: 'User email' })}
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            className="sm:max-w-xs"
          />
          <Select
            value={invitePlatform}
            onValueChange={(v) => setInvitePlatform(v as 'youtube' | 'instagram' | 'tiktok')}
          >
            <SelectTrigger className="sm:w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="youtube">YouTube</SelectItem>
              <SelectItem value="instagram">Instagram</SelectItem>
              <SelectItem value="tiktok">TikTok</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder={t('admin.campaigns.channelRequests.invite.channelUrlPlaceholder', { defaultValue: 'Channel / profile URL' })}
            value={inviteChannelUrl}
            onChange={(e) => setInviteChannelUrl(e.target.value)}
            className="flex-1"
          />
          <Button onClick={onInvite} disabled={inviteLoading} size="sm">
            {t('admin.campaigns.channelRequests.invite.button', { defaultValue: 'Add user' })}
          </Button>
        </div>
        {inviteSuccess && <p className="text-xs text-emerald-600">{inviteSuccess}</p>}
      </div>

      <div className="flex gap-3 items-center flex-wrap">
        <div className="flex items-center gap-1 border border-border rounded-md p-1">
          {STATUS_FILTERS.map((s) => (
            <Button
              key={s}
              variant={statusFilter === s ? 'default' : 'ghost'}
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={() => handleStatusFilterChange(s)}
            >
              {t(`admin.campaigns.channelRequests.statusFilter.${s}`)}
            </Button>
          ))}
        </div>
        <Input
          placeholder={t('admin.campaigns.channelRequests.searchPlaceholder', { defaultValue: 'Search' })}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
          className="ml-auto max-w-xs"
        />
      </div>

      {loading && rows.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-center py-10 text-muted-foreground">
          {t('admin.campaigns.channelRequests.empty')}
        </p>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.campaigns.channelRequests.columns.user')}</TableHead>
                <TableHead>{t('admin.campaigns.channelRequests.columns.platform')}</TableHead>
                <TableHead>{t('admin.campaigns.channelRequests.columns.channel')}</TableHead>
                <TableHead>{t('admin.campaigns.channelRequests.columns.submittedAt')}</TableHead>
                <TableHead>{t('admin.campaigns.channelRequests.columns.status')}</TableHead>
                <TableHead>{t('admin.campaigns.channelRequests.columns.reviewer')}</TableHead>
                <TableHead className="w-[200px]">
                  {t('admin.campaigns.channelRequests.columns.actions')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <Fragment key={r.id}>
                  <TableRow>
                    <TableCell>
                      <div className="text-sm">
                        <p className="font-medium">{r.user_name || '—'}</p>
                        <p className="text-xs text-muted-foreground">{r.user_email || '—'}</p>
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs font-semibold uppercase px-2 py-1 rounded bg-primary/10 text-primary">
                        {r.platform}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {r.youtube_channel?.thumbnail && (
                          <img
                            src={r.youtube_channel.thumbnail}
                            alt=""
                            className="w-6 h-6 rounded-full"
                          />
                        )}
                        <a
                          href={r.submitted_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:underline"
                        >
                          {r.handle || r.submitted_url}
                        </a>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-muted-foreground">
                      {formatDateTime(r.createdAt)}
                    </TableCell>
                    <TableCell className="text-xs uppercase">{r.status}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {r.reviewed_by_name
                        ? `${r.reviewed_by_name} • ${formatDateTime(r.reviewed_at)}`
                        : '—'}
                    </TableCell>
                    <TableCell className="space-x-2 whitespace-nowrap">
                      {r.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => setOpenInline((s) => ({ ...s, [r.id]: 'approve' }))}
                          >
                            {t('admin.campaigns.channelRequests.approve')}
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => setOpenInline((s) => ({ ...s, [r.id]: 'reject' }))}
                          >
                            {t('admin.campaigns.channelRequests.reject')}
                          </Button>
                        </>
                      )}
                      {r.status === 'approved' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setOpenInline((s) => ({ ...s, [r.id]: 'revoke' }))}
                        >
                          {t('admin.campaigns.channelRequests.revoke')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {(r.review_reason && r.status !== 'pending') && (
                    <TableRow>
                      <TableCell
                        colSpan={COL_SPAN}
                        className="text-xs text-muted-foreground pt-0 pb-2"
                      >
                        <span className="font-medium">{t('admin.campaigns.channelRequests.reasonLabel')}:</span>{' '}
                        {r.review_reason}
                      </TableCell>
                    </TableRow>
                  )}
                  {openInline[r.id] && (
                    <TableRow>
                      <TableCell colSpan={COL_SPAN}>
                        <div
                          className={
                            openInline[r.id] === 'approve'
                              ? 'border border-emerald-500/30 rounded-lg p-3 space-y-2 bg-emerald-500/5'
                              : 'border border-red-500/30 rounded-lg p-3 space-y-2 bg-red-500/5'
                          }
                        >
                          <div className="flex gap-2 items-center">
                            <Input
                              placeholder={
                                openInline[r.id] === 'approve'
                                  ? t('admin.campaigns.channelRequests.approveReasonPlaceholder')
                                  : t('admin.campaigns.channelRequests.reasonPlaceholder')
                              }
                              value={reviewReason[r.id] || ''}
                              onChange={(e) =>
                                setReviewReason((s) => ({ ...s, [r.id]: e.target.value }))
                              }
                              className="flex-1"
                            />
                            <Button
                              size="sm"
                              variant={openInline[r.id] === 'approve' ? 'default' : 'destructive'}
                              onClick={() => {
                                const action = openInline[r.id];
                                if (action === 'approve') onApprove(r.id);
                                else if (action === 'reject') onReject(r.id);
                                else if (action === 'revoke') onRevoke(r.id);
                              }}
                            >
                              {openInline[r.id] === 'approve'
                                ? t('admin.campaigns.channelRequests.confirmApprove')
                                : openInline[r.id] === 'reject'
                                  ? t('admin.campaigns.channelRequests.reject')
                                  : t('admin.campaigns.channelRequests.revoke')}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setOpenInline((s) => ({ ...s, [r.id]: null }))}
                            >
                              {t('common.cancel', { defaultValue: 'Cancel' })}
                            </Button>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              ))}
            </TableBody>
          </Table>
          {totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 border-t px-3 py-3">
              <span className="text-sm text-muted-foreground">
                {t('admin.common.page')} {page} {t('admin.common.of')} {totalPages}
                {total > 0 ? ` · ${total} ${t('admin.campaigns.channelRequests.totalLabel', { defaultValue: 'requests' })}` : ''}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  data-testid="channel-requests-previous-page"
                  aria-label={t('admin.campaigns.channelRequests.previousPage', { defaultValue: 'Previous page' })}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  data-testid="channel-requests-next-page"
                  aria-label={t('admin.campaigns.channelRequests.nextPage', { defaultValue: 'Next page' })}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      <Dialog open={!!errorMessage} onOpenChange={() => setErrorMessage(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.error', { defaultValue: 'Error' })}</DialogTitle>
          </DialogHeader>
          <p>{errorMessage}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
